import { useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Box, Typography, Card, CardContent, CardHeader,
  useTheme, useMediaQuery, Grid, TextField, InputAdornment, IconButton,
  Chip, Tooltip, Button, Paper, Skeleton, Alert, Stack
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { 
  Search as SearchIcon, 
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';

interface SaleRow {
  id: string;
  customer_name: string;
  total_amount: number;
  discount: number;
  notes: string;
  created_at: string;
  date: string;
}

export function Transactions(): JSX.Element {
  const [sales, setSales] = useState<SaleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('sales')
        .select('*');
        
      if (error) throw error;

      const formattedSales = (data || []).map(sale => ({
        ...sale,
        date: format(new Date(sale.created_at), 'MM/dd/yyyy')
      }));
      setSales(formattedSales);
    } catch (error) {
      setError('An unexpected error occurred');
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique clients for dropdown
  const uniqueClients = useMemo(() => {
    return Array.from(new Set(sales.map(s => s.customer_name))).sort();
  }, [sales]);

  // Filter sales based on search term
  const filteredSales = useMemo(() => {
    return sales.filter(sale => {
      const matchesSearch = sale.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            sale.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesClient = clientFilter ? sale.customer_name === clientFilter : true;
      const matchesMinAmt = minAmount ? Number(sale.total_amount) >= Number(minAmount) : true;
      const matchesMaxAmt = maxAmount ? Number(sale.total_amount) <= Number(maxAmount) : true;
      
      let matchesStart = true;
      let matchesEnd = true;
      if (startDate) {
        matchesStart = new Date(sale.created_at) >= new Date(startDate);
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setDate(end.getDate() + 1);
        matchesEnd = new Date(sale.created_at) < end;
      }
      return matchesSearch && matchesClient && matchesMinAmt && matchesMaxAmt && matchesStart && matchesEnd;
    });
  }, [sales, searchTerm, clientFilter, minAmount, maxAmount, startDate, endDate]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    return {
      totalSales: filteredSales.length,
      totalRevenue: filteredSales.reduce((sum, sale) => sum + Number(sale.total_amount), 0),
      averageSale: filteredSales.length ? 
        filteredSales.reduce((sum, sale) => sum + Number(sale.total_amount), 0) / filteredSales.length : 0,
      totalCustomers: new Set(filteredSales.map(sale => sale.customer_name)).size
    };
  }, [filteredSales]);

  const handleRefresh = () => {
    fetchSales();
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const columns: GridColDef[] = [
    { 
      field: 'customer_name', 
      headerName: 'Customer', 
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'date',
      headerName: 'Date',
      minWidth: 120,
      flex: 0.7,
      renderCell: (params) => (
        <Chip 
          icon={<DateRangeIcon fontSize="small" />}
          label={params.value}
          size="small"
          sx={{ 
            backgroundColor: theme.palette.background.default,
            '& .MuiChip-label': { fontWeight: 500 }
          }}
        />
      )
    },
    {
      field: 'total_amount',
      headerName: 'Amount',
      minWidth: 130,
      flex: 0.8,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          fontWeight={600}
          color="success.main"
        >
          ₹{Number(params.value).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'discount',
      headerName: 'Discount',
      minWidth: 120,
      flex: 0.7,
      valueFormatter: (params) => `₹${Number(params.value).toFixed(2)}`,
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1.2,
      minWidth: 200,
      valueGetter: (params) => params.row.notes || '-',
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography 
            variant="body2" 
            sx={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      )
    }
  ];

  return (
    <Box sx={{ pt: 2, px: { xs: 1, sm: 2, md: 3 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ 
            p: 2, 
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">Total Sales</Typography>
              <WalletIcon />
            </Box>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
              {loading ? <Skeleton width={60} /> : summaryStats.totalSales}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ 
            p: 2, 
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.contrastText
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">Revenue</Typography>
              <TrendingUpIcon />
            </Box>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
              {loading ? <Skeleton width={80} /> : `₹${summaryStats.totalRevenue.toFixed(2)}`}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ 
            p: 2, 
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.info.light,
            color: theme.palette.info.contrastText
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">Avg. Sale</Typography>
              <TrendingUpIcon />
            </Box>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
              {loading ? <Skeleton width={80} /> : `₹${summaryStats.averageSale.toFixed(2)}`}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ 
            p: 2, 
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">Customers</Typography>
              <PersonIcon />
            </Box>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
              {loading ? <Skeleton width={60} /> : summaryStats.totalCustomers}
            </Typography>
          </Paper>
        </Grid>
      </Grid>



      <Card elevation={0} sx={{ 
        borderRadius: 3, 
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)', 
        overflow: 'visible' 
      }}>
        <CardHeader
          sx={{ 
            px: 3, 
            py: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}` 
          }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WalletIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                Sales Transactions
              </Typography>
            </Box>
          }
          action={
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="Search transactions..."
                size="small"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearch}
                sx={{ 
                  width: { xs: 150, sm: 200 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 8
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Refresh data">
                <IconButton 
                  onClick={handleRefresh} 
                  color="primary"
                  sx={{ 
                    backgroundColor: theme.palette.background.default,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Client"
              size="small"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              sx={{ minWidth: 150 }}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">All Clients</option>
              {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
            </TextField>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Min Amount"
              type="number"
              size="small"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              sx={{ width: 120 }}
            />
            <TextField
              label="Max Amount"
              type="number"
              size="small"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              sx={{ width: 120 }}
            />
          </Box>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ display: 'flex', my: 1.5 }}>
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
            </Box>
          ) : (
            <DataGrid
              rows={filteredSales}
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              density={isMobile ? "compact" : "standard"}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                sorting: {
                  sortModel: [{ field: 'date', sort: 'desc' }],
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: theme.palette.background.default,
                  borderRadius: 1,
                },
                '& .MuiDataGrid-cell': {
                  py: 1.5
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: theme.palette.action.hover
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                  backgroundColor: theme.palette.background.default,
                },
                // Mobile optimizations
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'auto',
                },
                '& .MuiDataGrid-footerContainer': {
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? 1 : 0
                }
              }}
              disableColumnMenu={isMobile}
              slots={{
                toolbar: isMobile ? undefined : GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: !isTablet }
                }
              }}
            />
          )}
          
          {!loading && filteredSales.length === 0 && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 4,
              color: theme.palette.text.secondary
            }}>
              <SearchIcon sx={{ fontSize: 40, mb: 2, opacity: 0.7 }} />
              <Typography variant="h6">No transactions found</Typography>
              <Typography variant="body2">
                Try adjusting your search or filters
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                sx={{ mt: 2 }}
                onClick={handleRefresh}
              >
                Reset Filters
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
